import type { QueryClient } from '@tanstack/react-query'
import type { Chat } from '../../app/modules/types/chatBot'
import { CHAT_PAGE_SIZE } from '../../app/pages/chat-history/chatPageState'
import { queryKeys } from '../../lib/react-query/queryKeys'

/**
 * The cache entry `ChatHistoryPage` actually subscribes to, defined ONCE.
 *
 * The tour seeds this key and clears it afterwards, from two different places in
 * `tour/index.tsx`. Keeping two copies of the expression in sync by hand is the
 * bug that broke this file in the first place — a seed key that no longer
 * matches is completely silent, and a *teardown* key that no longer matches is
 * worse: the mock rows outlive the tour and the real page renders them as if
 * they were the org's own chats.
 *
 * `limit` is `CHAT_PAGE_SIZE + 1` because the page over-fetches by one row to
 * detect "is there more" — §10 gives no total to page by. Imported rather than
 * written as 21, so changing the page size cannot orphan this entry. Passing the
 * params OBJECT is likewise deliberate: react-query hashes plain-object keys
 * with the keys sorted, so an equal object hashes equal.
 */
export const mockChatListKey = (orgId: string) =>
  queryKeys.chat.list(orgId, { limit: CHAT_PAGE_SIZE + 1, offset: 0 })

/**
 * Put the demo chats in the cache, and stop them being refetched away.
 *
 * The freeze is not optional here the way it is for most seeds.
 * `QUERY_CONFIG.CHAT.staleTime` is **0** — "always fresh", correct for a live
 * chat list — so seeded rows are stale the instant they land and `useChatList`
 * refetches on mount, replacing the demo list with the org's real one (usually
 * empty on a demo account) before the user reaches step two. Same guard
 * `seedCreateScanMock` uses on the scan keys; undone by `clearChatListSeed`.
 *
 * Both halves live here rather than at the call site so the test can exercise
 * exactly what the tour does.
 */
export function seedChatList(queryClient: QueryClient, orgId: string): void {
  const key = mockChatListKey(orgId)
  queryClient.setQueryDefaults(key, { staleTime: Infinity, gcTime: Infinity })
  queryClient.setQueryData(key, MOCK_CHAT_LIST)
}

/**
 * Undo `seedChatList`: drop the demo rows and hand the key back to the normal
 * caching rules, so the real page refetches like any other.
 *
 * Resetting the defaults matters as much as clearing the data — leaving
 * `staleTime: Infinity` behind would freeze the user's real chat list for the
 * rest of the session, which is a worse bug than the one the tour had.
 *
 * `removeQueries`, NOT `setQueryData(key, undefined)`. In react-query v5 an
 * `undefined` value means "no change" and the write is a silent no-op — the
 * generic teardown loop in `tour/index.tsx` gets away with it only because the
 * `invalidateQueries` after it refetches ACTIVE observers over the top. With no
 * observer mounted, the demo rows would simply stay in the cache and be served
 * to the real page the next time it opened.
 */
export function clearChatListSeed(queryClient: QueryClient, orgId: string): void {
  const key = mockChatListKey(orgId)
  queryClient.setQueryDefaults(key, {})
  queryClient.removeQueries({ queryKey: key, exact: true })
}

/**
 * The chat list the product tour's `aiChat` step renders on `/chat-history`.
 *
 * Service-shaped, not Django-shaped. This used to be
 * `{results, total_pages, current_page, count}` with an embedded `vulnerability`
 * summary per row, seeded under `[orgId,'chat','list','','page',1]`. The
 * chatbot-service migration replaced BOTH halves: `useChatList` resolves a bare
 * `Chat[]` (there is no envelope, no total and no page count on any collection —
 * §10), and `queryKeys.chat.list` now takes the limit/offset params object. The
 * old seed therefore sat in the cache under a key nothing subscribes to, and the
 * tour's chat step showed an empty list.
 *
 * Typed as `Chat[]` on purpose: the next wire change breaks the build here
 * instead of silently emptying the tour again.
 *
 * Note what is NOT here any more — the service holds no scan-domain knowledge
 * and cannot serve a finding's title or severity (§17). The list card renders
 * `title`, which the backend derives once from the first user message (§6), plus
 * a badge off `subject_type`. So the titles below are written as fragments of a
 * user's opening question, which is what real rows look like, and one row is a
 * free chat so the tour shows both the anchored and the unanchored card.
 */
export const MOCK_CHAT_LIST: Chat[] = [
  {
    id: 'mock-chat-1',
    organization_id: 'mock-org',
    user_id: 'mock-user',
    subject_type: 'finding',
    subject_id: 'mock-finding-1',
    title: 'How do I fix the SQL injection in the login form?',
    is_helpful: null,
    created_at: '2024-11-15T09:20:00Z',
    updated_at: '2024-11-15T09:24:00Z',
  },
  {
    id: 'mock-chat-2',
    organization_id: 'mock-org',
    user_id: 'mock-user',
    subject_type: 'finding',
    subject_id: 'mock-finding-2',
    title: 'Is this reflected XSS exploitable without a login?',
    is_helpful: true,
    created_at: '2024-11-15T09:15:00Z',
    updated_at: '2024-11-15T09:18:00Z',
  },
  {
    // Free chat: no subject, so the card shows the plain icon and no badge.
    id: 'mock-chat-3',
    organization_id: 'mock-org',
    user_id: 'mock-user',
    subject_type: null,
    subject_id: null,
    title: 'What should we prioritise from this week’s scan?',
    is_helpful: null,
    created_at: '2024-11-15T09:12:00Z',
    updated_at: '2024-11-15T09:13:00Z',
  },
]
